import { useState, useCallback } from 'react'
import { useAppStore } from '../../store/appStore'
import { apiUrl, authFetch } from '../../lib/api'

interface Issue {
  file: string; line: number; severity: string; category: string
  title: string; description: string; fix: string
}

export function SecurityScannerPanel({ onClose: _onClose }: { onClose: () => void }) {
  const [loading, setLoading] = useState(false)
  const [issues, setIssues] = useState<Issue[]>([])
  const [scanned, setScanned] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const runScan = useCallback(async () => {
    const { openTabs } = useAppStore.getState()
    const files = openTabs.map(t => ({ path: t.path.replace(/^\//, ''), content: t.content }))
    if (files.length === 0) { setError('No files open. Open some files first.'); return }

    setLoading(true); setIssues([]); setError(null); setScanned(false)
    try {
      const res = await authFetch(apiUrl('/api/ai-tools/scan'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ files }),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        setError(`Server error ${res.status}: ${text.slice(0, 200)}`)
        return
      }
      const data = await res.json()
      setIssues(data.issues ?? [])
      setScanned(true)
    } catch (e: any) {
      setError(`Request failed: ${e?.message ?? 'Unknown error'}. Is the server running?`)
    } finally { setLoading(false) }
  }, [])

  const severityColors: Record<string, string> = {
    critical: '#ff4444', high: 'var(--red)', medium: 'var(--accent-3)', low: 'var(--accent)',
  }
  const categoryIcons: Record<string, string> = {
    security: '🔒', bug: '🐛', performance: '⚡', style: '✨',
  }

  const grouped = issues.reduce((acc, i) => {
    acc[i.severity] = (acc[i.severity] ?? 0) + 1; return acc
  }, {} as Record<string, number>)

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <span style={{ fontSize: 14 }}>🛡</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 11, color: 'var(--text-0)' }}>Security Scanner</span>
        <div style={{ flex: 1 }} />
        <button onClick={runScan} disabled={loading} style={{
          padding: '4px 12px', borderRadius: 6, border: '1px solid rgba(124,110,245,0.3)',
          background: 'rgba(124,110,245,0.1)', color: 'var(--accent)',
          cursor: 'pointer', fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 700,
          opacity: loading ? 0.5 : 1,
        }}>
          {loading ? 'Scanning...' : '🛡 Scan Open Files'}
        </button>
      </div>

      {/* Summary */}
      {scanned && (
        <div style={{ padding: '8px 14px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 12 }}>
          {Object.entries(grouped).map(([sev, count]) => (
            <span key={sev} style={{ fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 700, color: severityColors[sev] ?? 'var(--text-2)' }}>
              {count} {sev}
            </span>
          ))}
          {issues.length === 0 && <span style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--accent-2)', fontWeight: 700 }}>✓ No issues found</span>}
        </div>
      )}

      <div style={{ flex: 1, overflow: 'auto' }}>
        {error && (
          <div style={{ padding: '10px 14px', background: 'rgba(240,112,112,0.08)', borderBottom: '1px solid rgba(240,112,112,0.2)' }}>
            <div style={{ fontSize: 11, color: 'var(--red)', fontFamily: 'var(--font-mono)' }}>⚠ {error}</div>
          </div>
        )}
        {loading && (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--accent)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
            Scanning for vulnerabilities with AI... this may take a few seconds
          </div>
        )}
        {!scanned && !loading && !error && (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-2)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
            Scan open files for security vulnerabilities, bugs, and code quality issues
          </div>
        )}
        {issues.map((issue, i) => (
          <div key={i} style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <span>{categoryIcons[issue.category] ?? '📌'}</span>
              <span style={{
                fontSize: 8, padding: '2px 6px', borderRadius: 4, fontFamily: 'var(--font-mono)', fontWeight: 700,
                background: `${severityColors[issue.severity] ?? 'var(--text-2)'}20`,
                color: severityColors[issue.severity] ?? 'var(--text-2)',
              }}>{issue.severity.toUpperCase()}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--text-0)' }}>{issue.title}</span>
            </div>
            <div style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginBottom: 4 }}>
              {issue.file}:{issue.line}
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-1)', fontFamily: 'var(--font-mono)', lineHeight: 1.5 }}>{issue.description}</div>
            {issue.fix && (
              <div style={{ fontSize: 10, color: 'var(--accent-2)', fontFamily: 'var(--font-mono)', marginTop: 4, padding: '4px 8px', background: 'rgba(95,212,164,0.06)', borderRadius: 4 }}>
                💡 {issue.fix}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
